/**
 * DE-313 / IM-004: Hydrate cover→FG inventory for all displayed swatches with the
 * PDP warehouse `inventoryIds`. SFRA `linkedInventory` is site-default only; when a
 * shopper warehouse is active, replace that snapshot before swatch labels / preselect.
 */

import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';

import {usePdpShopperInventoryIds} from '@amblique/king-living/app/hooks/use-pdp-shopper-inventory-ids';
import {
  chunk,
  GET_PRODUCTS_PAGE_SIZE,
  GET_PRODUCTS_PARAMS,
  GET_PRODUCTS_STALE_TIME_MS,
  mapWithConcurrency,
} from '@amblique/king-living/app/utils/api-utils';
import {
  collectCoverLinkedFinishedGoodIds,
  mergeCoverVariantsWithWarehouseInventory,
} from '@amblique/king-living/app/utils/inventory-utils';
import {useCoverSwatchPimlyImages} from '@amblique/king-living/app/hooks/use-cover-swatch-pimly-images';

/**
 * @param {Object} options
 * @param {Array<Object>} options.variants - Cover entries from `c_allVariantsData`
 * @param {boolean} [options.enabled=true]
 * @returns {{
 *   variants: Array<Object>,
 *   isPending: boolean,
 *   productsById: Record<string, Object>,
 * }}
 */
export function useCoverSwatchWarehouseInventory({variants, enabled = true}) {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const {shopperInventoryIds, isInventoryIdsResolving} = usePdpShopperInventoryIds();

  const warehouseActive = Boolean(enabled && shopperInventoryIds);
  const sourceVariants = Array.isArray(variants) ? variants : [];

  const {variants: pimlyVariants, isPending: isPimlyPending} = useCoverSwatchPimlyImages({
    enabled,
    variants: sourceVariants,
  });

  const fgIds = useMemo(
    () => (warehouseActive ? collectCoverLinkedFinishedGoodIds(pimlyVariants) : []),
    [warehouseActive, pimlyVariants]
  );

  const idsKey = fgIds.join(',');
  const invKey = shopperInventoryIds ?? '';
  const shouldFetch = warehouseActive && fgIds.length > 0 && !isInventoryIdsResolving;

  const {data, isLoading, isFetching, isFetched} = useQuery({
    queryKey: ['king-living/cover-swatch-fg-inventory', idsKey, invKey],
    queryFn: async () => {
      if (fgIds.length === 0) return [];

      const token = await getTokenWhenReady();
      const chunks = chunk(fgIds, GET_PRODUCTS_PAGE_SIZE);
      // Bounded fan-out so swatch hydration cannot monopolise the browser's
      // connection pool ahead of the CTA's own inventory request.
      const results = await mapWithConcurrency(chunks, (ids) => api.shopperProducts
        .getProducts({
          parameters: {
            ids: ids.join(','),
            ...GET_PRODUCTS_PARAMS,
            inventoryIds: shopperInventoryIds,
          },
          headers: {Authorization: `Bearer ${token}`},
        })
        .then((res) => res?.data ?? []));

      return results.flat();
    },
    enabled: shouldFetch,
    staleTime: GET_PRODUCTS_STALE_TIME_MS,
    retry: 1,
  });

  const productsById = useMemo(() => {
    const map = {};

    (data || []).forEach((item) => {
      if (item?.id) {
        map[item.id] = item;
      }
    });

    return map;
  }, [data]);

  const hydrationComplete = !shouldFetch || (isFetched && !isLoading && !isFetching);

  const hydratedVariants = useMemo(() => {
    if (!warehouseActive) {
      return pimlyVariants;
    }

    return mergeCoverVariantsWithWarehouseInventory(
      pimlyVariants,
      productsById,
      shopperInventoryIds,
      {hydrationComplete}
    );
  }, [
    warehouseActive,
    pimlyVariants,
    productsById,
    shopperInventoryIds,
    hydrationComplete
  ]);

  const isPending =
    Boolean(enabled) &&
    (isPimlyPending ||
      (warehouseActive &&
        (isInventoryIdsResolving || (fgIds.length > 0 && !hydrationComplete))));

  return {
    variants: hydratedVariants,
    isPending,
    productsById,
  };
}

export default useCoverSwatchWarehouseInventory;
